import {Menu, MenuButton, MenuItem, MenuItems} from "@headlessui/react";
import {useState, useEffect} from "react";
import {BsFilter, BsSearch} from "react-icons/bs";
import {TableData} from "../../../components/TableData";
import {StatusCard} from "../../../components/StatusCard";
import {useLocation, useNavigate} from "react-router-dom";
import {EmptyState} from "../../../components/EmptyState";
import axios from "axios";
import {useQuery} from "react-query";
import {getStatusType} from "../../../utils/status";
import moment from "moment";

const TABLE_HEAD = [
  //   "",
  "Created at",
  "Sender",
  "Order ID",
  "Pickup Address",
  "City",
  "Pickup Date",
  "Phone number",
  "Delivery Type",
  "Status",
];

const DELIVERY_TYPE_OPTIONS = [
  {label: "All", value: ""},
  {label: "Standard", value: "STANDARD"},
  {label: "Express", value: "EXPRESS"},
  {label: "Same Day", value: "SAME_DAY"},
];

export const FailedPickupsTable = ({
  selectedPickups,
  setSelectedPickups,
  hasSubmitted,
  setHasSubmitted,
}: {
  selectedPickups: any[];
  setSelectedPickups: React.Dispatch<React.SetStateAction<any[]>>;
  setHasSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  hasSubmitted: boolean;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const token = JSON.parse(localStorage.getItem("eftd-admin-token") || "{}")
    ?.data?.data?.token;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("Filter");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState("");

  // Get current page from URL or default to 1
  const queryParams = new URLSearchParams(location.search);
  const currentPage = Number(queryParams.get("page")) || 1;

  const config = {
    headers: {Authorization: `Bearer ${token}`},
  };

  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  const handleSelectDelivery = (value: any) => {
    setSelectedPickups((prev) => {
      if (prev.some((pickup) => pickup.id === value.id)) {
        return prev.filter((v) => v.id !== value.id);
      } else {
        return [...prev, value];
      }
    });
  };

  const getFailedPickups = async () => {
    let url = `${Baseurl}/api/admin/orders/pickups?limit=${limit}&page=${currentPage}&filter=status:FAILED&sort=pickupDate:DESC`;

    if (debouncedSearchQuery) {
      url += `&query=${encodeURIComponent(debouncedSearchQuery)}`;
    }

    if (deliveryTypeFilter) {
      url += `&filter=deliveryType:${deliveryTypeFilter}`;
    }

    const response = await axios.get(url, config);
    setTotalPages(Math.ceil(response?.data?.data?.meta?.total / limit));
    return response?.data?.data?.items;
  };

  const {
    data: failedPickups,
    isLoading: isLoadingFailedPickups,
    refetch: refetchFailed,
    isRefetching: isRefetchingFailedPickups,
  } = useQuery(
    [
      "FAILED_PICKUPS",
      currentPage,
      limit,
      debouncedSearchQuery,
      deliveryTypeFilter,
    ],
    getFailedPickups,
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    refetchFailed();
  }, [
    debouncedSearchQuery,
    deliveryTypeFilter,
    currentPage,
    limit,
    refetchFailed,
  ]);

  useEffect(() => {
    if (hasSubmitted) {
      refetchFailed();
      setHasSubmitted(false);
    }
  }, [hasSubmitted, refetchFailed, setHasSubmitted]);

  const handlePageChange = (newPage: number) => {
    navigate(`?page=${newPage}`);
  };

  const handleDeliveryTypeFilter = (value: string, label: string) => {
    setDeliveryTypeFilter(value);
    setSelectedLabel(label === "All" ? "Filter" : label);
    handlePageChange(1); // Reset to first page when filter changes
  };

  const containerStyle = {
    width: "100%",
    borderRadius: "8px",
    border: "1px solid #e4e4e7",
  };

  const tableRowStyle = {
    borderBottom: "1px solid #eaecf0",
  };

  return (
    <div style={containerStyle}>
      <div className="flex items-center justify-between !px-6 !py-4">
        <h3 className="!text-xl !font-bold text-gray-700">Failed Pickups</h3>
        <div className="flex gap-3">
          {/* Search */}
          <div className="relative !h-12 !bg-gray-50 !border !rounded-[10px] !w-[428px] !border-gray-300">
            <input
              type="text"
              placeholder="Search failed pickups"
              className="w-full h-full focus:!outline-none !pl-14 placeholder:!text-black/30 !text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <span className="!absolute !top-3.5 !left-6">
              <BsSearch />
            </span>
          </div>

          {/* Filter dropdown */}
          <Menu as="div" className="relative">
            <MenuButton className="flex items-center gap-2 py-3 px-3 text-sm font-medium border rounded-lg border-gray-300 text-gray-600">
              <BsFilter />
              <span>{selectedLabel}</span>
            </MenuButton>
            <MenuItems className="absolute right-0 z-10 w-48 mt-2 bg-white border border-gray-200 rounded-md shadow-lg">
              {DELIVERY_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value}>
                  {({active}) => (
                    <button
                      className={`block w-full p-2 text-left text-sm ${
                        active ? "bg-gray-100" : ""
                      }`}
                      onClick={() =>
                        handleDeliveryTypeFilter(option.value, option.label)
                      }
                    >
                      {option.label}
                    </button>
                  )}
                </MenuItem>
              ))}
            </MenuItems>
          </Menu>
        </div>
      </div>

      {isLoadingFailedPickups || isRefetchingFailedPickups ? (
        <div style={{padding: "56px 0"}}>
          <p>Loading...</p>
        </div>
      ) : failedPickups?.length > 0 ? (
        <>
          <div className="!overflow-auto">
            <table className="!w-full !min-w-max !table-auto !text-left">
              <thead>
                <tr className="!border-b !border-b-[#eaecf0] !bg-[#f9fafb]">
                  {TABLE_HEAD.map((head) => (
                    <th
                      key={head}
                      className="!text-xs !font-medium !text-[#667085] !p-3"
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {failedPickups.map((order: any) => {
                  const status = getStatusType(order.status);
                  const formattedDate = new Date(
                    order.pickupDate
                  ).toLocaleDateString("en-US", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  });

                  return (
                    <tr
                      key={order.id}
                      style={tableRowStyle}
                      className="border-b border-gray-100 hover:bg-gray-100 transition-colors"
                      onClick={() => handleSelectDelivery(order)}
                    >
                      {/* <td style={{padding: "24px 16px"}}>
                        <input
                          type="checkbox"
                          id={order.id}
                          name="user"
                          value={order.id}
                          className="w-4 h-4 accent-orange-500 border-gray-200"
                          checked={selectedPickups.some(
                            (pickup) => pickup.id === order.id
                          )}
                          onClick={() => {
                            handleSelectDelivery(order);
                          }}
                          onClickCapture={() => handleSelectDelivery(order)}
                        />
                      </td> */}
                      <TableData
                        content={
                          order.createdAt ? (
                            <>
                              {moment(order.createdAt).format("DD MMM, YYYY")}
                              <br />
                              {moment(order.createdAt).format("hh:mmA")}
                            </>
                          ) : (
                            "N/A"
                          )
                        }
                      />

                      <TableData content={order.senderName} />
                      <TableData content={order.orderId} />
                      <td className="!text-[13px] !font-medium !text-gray-600 !py-5 !px-3 text-wrap max-w-[200px]">
                        {`${order.address}`}
                      </td>
                      <TableData
                        content={
                          order.city && order.city !== "" ? order.city : "N/A"
                        }
                      />
                      <TableData content={formattedDate} />
                      <TableData content={order.phone} />
                      <TableData content={order.deliveryType} />
                      <td style={{padding: "20px 12px"}}>
                        <StatusCard title={status.title} type={status.type} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="flex items-center gap-6">
              <p className="text-sm text-gray-500">
                Showing page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Previous
                </button>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-md border ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-500">Items per page:</p>
              <select
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  handlePageChange(1);
                }}
                className="border border-gray-500 rounded py-1 px-2 text-sm text-gray-500"
                value={limit}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="30">30</option>
                <option value="40">40</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : (
        <div style={{padding: "56px 0"}}>
          <EmptyState type="order" text="No Failed Pickups Found" />
        </div>
      )}
    </div>
  );
};
